import { isBrowser, isMobile } from 'react-device-detect';
import { WebStorageStateStore } from 'oidc-client';
import createUserManager from './createUserManager';
import ynToBool from '../../../Utils/ynToBool';
import axios from 'axios';
import jwt_decode from "jwt-decode";
import Cookies from 'js-cookie';
import * as AuthService from "Services/Apis/Auth";

let redirectUrl = '/';

const decryptSSO = async (sso) => {
  const message = await AuthService.decrypt(sso);
  const { code, codeVerifier } = message;

  const hasRedirectUrl = message?.redirectUrl;

  if (hasRedirectUrl) redirectUrl = new URL(hasRedirectUrl).pathname;

  return { code, codeVerifier };
}

const getCodeAndCodeVerifierFromURL = async () => {
  const urlParams = new URLSearchParams(location.search);
  const sso = urlParams.get('sso');

  if (!sso) return null;

  return await decryptSSO(sso);
};

const isSilentRenewEnabled = () => {
  let enabled = false;

  if (ynToBool(process.env.REACT_APP_SILENT_RENEW)) {
    enabled =
      (isMobile && process.env.REACT_APP_SILENT_RENEW_PLATFORMS.includes('MOBILE')) ||
      (isBrowser && process.env.REACT_APP_SILENT_RENEW_PLATFORMS.includes('BROWSER'));
  }

  return enabled;
};

const getLogoutUri = () => {
  const logoutUri = process.env.REACT_APP_LOGOUT_URI;

  if (!logoutUri) return null;

  const url = new URL(logoutUri);
  const queryParams = new URLSearchParams(url.search);
  const extractedLogoutUri = queryParams.get('logout_uri');

  return extractedLogoutUri;
}

const exchangeAuthorizationCode = async (code, codeVerifier) => {
  const tokenEndpoint = process.env.REACT_APP_AUTH_ENDPOINT;

  try {
    const { data: dataUser } = await axios.post(
      `${tokenEndpoint}/connect/token`,
      new URLSearchParams({
        client_id: process.env.REACT_APP_CLIENT_ID,
        redirect_uri: window.location.origin,
        grant_type: 'authorization_code',
        code: code,
        code_verifier: codeVerifier,
      }),
      { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }
    );

    const { data: dataProfile } = await axios.get(`${tokenEndpoint}/connect/userinfo`, {
      headers: { Authorization: `Bearer ${dataUser.access_token}` },
    });

    const token = {
      access_token: dataUser.access_token,
      id_token: dataUser.id_token,
      expires_in: dataUser.expires_in,
      token_type: dataUser.token_type,
      scope: dataUser.scope,
      profile: { ...dataProfile },
    };

    return token;
  } catch (error) {
    console.error('Erro ao trocar o código pelo token:', error);

    const logoutUri = getLogoutUri();
    window.location.href = logoutUri || '/';
  }
};

const extractSessionId = (token) => {
  const { sid } = jwt_decode(token);

  Cookies.set('sid', sid, { expires: 1 });
}

const getUserStore = async () => {
  const authParams = await getCodeAndCodeVerifierFromURL();

  if (!authParams) {
    const existingUser = window.localStorage.getItem(`oidc.user:${process.env.REACT_APP_AUTH_ENDPOINT}/.well-known/openid-configuration:${process.env.REACT_APP_CLIENT_ID}`);

    if (existingUser || isSilentRenewEnabled())
      return new WebStorageStateStore({ store: window.localStorage });

    return new WebStorageStateStore({ store: window.sessionStorage });
  }

  const { code, codeVerifier } = authParams;

  let token = null;

  if (code && codeVerifier) {
    token = await exchangeAuthorizationCode(code, codeVerifier);

    if (token) {
      extractSessionId(token.access_token);

      window.localStorage.setItem(
        `oidc.user:${process.env.REACT_APP_AUTH_ENDPOINT}/.well-known/openid-configuration:${process.env.REACT_APP_CLIENT_ID}`,
        JSON.stringify(token)
      );

      return new WebStorageStateStore({ store: window.localStorage });
    }
  }
};

const getClientId = () => {
  if (isMobile) {
    return process.env.REACT_APP_CLIENT_ID_MOBILE || process.env.REACT_APP_CLIENT_ID;
  }

  return process.env.REACT_APP_CLIENT_ID;
};

const getResponseType = () => {
  if (isSilentRenewEnabled()) {
    return 'code';
  }

  return 'token id_token';
};

const initializeUserManager = async () => {
  const userStore = await getUserStore();

  const scope =
    process.env.REACT_APP_AUTH_SCOPE || `profile email openid aws.cognito.signin.user.admin webpremios.campaigns/${process.env.REACT_APP_TENANT_ID}`;

  const redirectUri = 
    `${window.location.protocol}//${window.location.hostname}${window.location.port ? `:${window.location.port}` : ''}/auth/callback`;

  const config = {
    authority: `${process.env.REACT_APP_AUTH_ENDPOINT}/.well-known/openid-configuration`,
    scope,
    client_id: getClientId(),
    userStore: userStore,
    response_type: getResponseType(),
    automaticSilentRenew: isSilentRenewEnabled(),
    redirect_uri: redirectUri,
    monitorSession: false,
  };

  console.log("🚀 ~ initializeUserManager ~ config:", config)

  return config;
};

export const getRedirectUrl = () => {
  return redirectUrl;
}

const userManagerConfig = await initializeUserManager();
export default createUserManager(userManagerConfig);